import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { calls as callsApi, shoppers as shoppersApi, surveys as surveysApi } from "../api/endpoints";
import type { Call, CallHistory, CallStatus, CallSurveyInfo, User } from "../types";

function scoreTone(score: number) {
  if (score >= 70) return "bg-emerald-100 text-emerald-800";
  if (score >= 50) return "bg-amber-100 text-amber-800";
  return "bg-rose-100 text-rose-800";
}

function statusTone(name: string) {
  if (name.includes("Completed")) return "bg-emerald-100 text-emerald-800";
  if (name.includes("Pending")) return "bg-amber-100 text-amber-800";
  if (name.includes("Cancelled")) return "bg-rose-100 text-rose-800";
  if (name.includes("Scheduled")) return "bg-sky-100 text-sky-800";
  if (name.includes("In progress")) return "bg-indigo-100 text-indigo-800";
  return "bg-slate-100 text-slate-700";
}

interface ShopperOption { id: number; name: string; }

export default function CallDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [call, setCall] = useState<Call | null>(null);
  const [history, setHistory] = useState<CallHistory[]>([]);
  const [surveyInfo, setSurveyInfo] = useState<CallSurveyInfo | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Email modals
  const [showReviewEmail, setShowReviewEmail] = useState(false);
  const [showStopEmail, setShowStopEmail] = useState(false);
  const [emailRecipientFields, setEmailRecipientFields] = useState<{ label: string; email: string }[]>([]);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailScore, setEmailScore] = useState<number | null>(null);
  const [emailWaveUrl, setEmailWaveUrl] = useState("");
  const [emailTemplateSource, setEmailTemplateSource] = useState("");

  // Editing state
  const [editing, setEditing] = useState(false);
  const [statuses, setStatuses] = useState<CallStatus[]>([]);
  const [shopperList, setShopperList] = useState<ShopperOption[]>([]);
  const [editFields, setEditFields] = useState({
    status_id: 0,
    shopper_id: null as number | null,
    scenario: "",
    notes: "",
    scheduled_call_date: "",
    earliest_call_date: "",
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    if (!id) return;
    const callId = Number(id);
    callsApi.get(callId).then((c) => {
      setCall(c);
      setEditFields({
        status_id: c.status.id,
        shopper_id: c.shopper_id || null,
        scenario: c.scenario || "",
        notes: c.notes || "",
        scheduled_call_date: c.scheduled_call_date || "",
        earliest_call_date: c.earliest_call_date || "",
      });
    });
    callsApi.history(callId).then(setHistory);
    surveysApi.forCall(callId).then(setSurveyInfo);
    callsApi.statuses().then(setStatuses);
    shoppersApi.list("limit=500").then((res) => {
      setShopperList(
        res.items
          .map((u: User) => ({ id: u.id, name: `${u.first_name} ${u.last_name}`.trim() }))
          .sort((a: ShopperOption, b: ShopperOption) => a.name.localeCompare(b.name))
      );
    });
  }, [id]);

  const openPdfReport = async () => {
    if (!id) return;
    setPdfLoading(true);
    setShowPdfModal(true);
    try {
      const blob = await surveysApi.generatePdf(Number(id));
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch {
      setPdfUrl(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdfModal = () => {
    setShowPdfModal(false);
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
  };

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  if (!call) {
    return (
      <div className="surface-panel px-6 py-10 text-center">
        <div className="eyebrow">Calls workspace</div>
        <p className="mt-3 text-lg text-slate-600">Loading call details...</p>
      </div>
    );
  }

  const displayCallId = call.legacy_id || call.id;
  const hasLegacyAlias = Boolean(call.legacy_id && call.legacy_id !== call.id);

  const handleReset = async () => {
    if (!window.confirm("Reset this call? This will clear the schedule, audio, and survey.")) return;
    try {
      const updated = await callsApi.reset(call.id);
      setCall(updated);
      setToast({ type: "success", message: "Call reset successfully." });
      callsApi.history(call.id).then(setHistory);
    } catch {
      setToast({ type: "error", message: "Failed to reset call." });
    }
  };

  const handleUploadAudio = async () => {
    if (!audioFile) return;
    try {
      const updated = await callsApi.uploadAudio(call.id, audioFile);
      setCall(updated);
      setAudioFile(null);
      setToast({ type: "success", message: "Audio uploaded." });
    } catch {
      setToast({ type: "error", message: "Audio upload failed." });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, any> = {};
      if (editFields.status_id !== call.status.id) payload.status_id = editFields.status_id;
      if (editFields.shopper_id !== (call.shopper_id || null)) payload.shopper_id = editFields.shopper_id;
      if (editFields.scenario !== (call.scenario || "")) payload.scenario = editFields.scenario;
      if (editFields.notes !== (call.notes || "")) payload.notes = editFields.notes;
      if (editFields.scheduled_call_date !== (call.scheduled_call_date || ""))
        payload.scheduled_call_date = editFields.scheduled_call_date || null;
      if (editFields.earliest_call_date !== (call.earliest_call_date || ""))
        payload.earliest_call_date = editFields.earliest_call_date || null;

      if (Object.keys(payload).length === 0) {
        setEditing(false);
        return;
      }

      const updated = await callsApi.update(call.id, payload);
      setCall(updated);
      setEditing(false);
      setToast({ type: "success", message: "Call updated." });
      callsApi.history(call.id).then(setHistory);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "Failed to update call.";
      setToast({ type: "error", message: typeof msg === "string" ? msg : JSON.stringify(msg) });
    } finally {
      setSaving(false);
    }
  };

  const openReviewEmailModal = async () => {
    if (!call) return;
    try {
      const preview = await surveysApi.emailPreview(call.id, "review");
      setEmailRecipientFields(preview.recipient_fields);
      setEmailSubject(preview.subject);
      setEmailBody(preview.body);
      setEmailScore(preview.score);
      setEmailWaveUrl(preview.wave_file_url);
      setEmailTemplateSource(preview.template_source);
    } catch {
      setEmailRecipientFields([]);
      setEmailSubject(
        `${call.sales_staff_name || "Staff"} - ${call.property_name} - Call ID ${call.shop_id || call.id}`
      );
      setEmailBody("");
      setEmailScore(null);
      setEmailWaveUrl("");
      setEmailTemplateSource("");
    }
    setShowReviewEmail(true);
  };

  const handleSendReviewEmail = async () => {
    if (!call) return;
    setEmailSending(true);
    try {
      const recipients = emailRecipientFields.map((r) => r.email).filter(Boolean);
      await surveysApi.sendEmail({
        call_id: call.id,
        recipients,
        subject: emailSubject,
        body: emailBody,
      });
      setShowReviewEmail(false);
      setToast({ type: "success", message: "Review email sent." });
      const updated = await callsApi.get(call.id);
      setCall(updated);
      callsApi.history(call.id).then(setHistory);
    } catch {
      setToast({ type: "error", message: "Failed to send review email." });
    } finally {
      setEmailSending(false);
    }
  };

  const openStopEmailModal = async () => {
    if (!call) return;
    try {
      const preview = await surveysApi.emailPreview(call.id, "stop");
      setEmailRecipientFields(preview.recipient_fields);
      setEmailSubject(preview.subject);
      setEmailBody(preview.body);
      setEmailScore(preview.score);
      setEmailWaveUrl(preview.wave_file_url);
      setEmailTemplateSource(preview.template_source);
    } catch {
      setEmailRecipientFields([]);
      setEmailSubject(
        `${call.sales_staff_name || "Staff"} shop call STOP PROCESS notification`
      );
      setEmailBody("");
      setEmailScore(null);
      setEmailWaveUrl("");
      setEmailTemplateSource("");
    }
    setShowStopEmail(true);
  };

  const handleSendStopEmail = async () => {
    if (!call) return;
    setEmailSending(true);
    try {
      const recipients = emailRecipientFields.map((r) => r.email).filter(Boolean);
      await surveysApi.sendTerminationEmail({
        call_id: call.id,
        recipients,
        subject: emailSubject,
        body: emailBody,
      });
      setShowStopEmail(false);
      setToast({ type: "success", message: "Stop process email sent." });
      const updated = await callsApi.get(call.id);
      setCall(updated);
      callsApi.history(call.id).then(setHistory);
    } catch {
      setToast({ type: "error", message: "Failed to send stop process email." });
    } finally {
      setEmailSending(false);
    }
  };

  const surveyReady = surveyInfo?.survey_id && !surveyInfo?.has_existing_response;
  const surveyDone = surveyInfo?.has_existing_response;
  const playableAudioUrl = call.audio_url || call.wave_file;

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 surface-panel px-5 py-3 text-sm font-semibold shadow-lg ${
            toast.type === "success" ? "border-l-4 border-emerald-500 text-emerald-800" : "border-l-4 border-rose-500 text-rose-800"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Hero header */}
      <section className="surface-hero rise-in px-6 py-7 md:px-8">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 flex-1">
            <button onClick={() => navigate("/calls")} className="button-ghost !px-4 !py-2 text-sm">
              Back to Calls
            </button>
            <div className="mt-5">
              <div className="eyebrow">Call dossier</div>
              <h1 className="page-title">Call #{displayCallId}</h1>
              {hasLegacyAlias && (
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  New system ID #{call.id}
                </p>
              )}
              <p className="page-copy">
                {call.property_name} ({call.property_hotel_code})
                {call.management_company_name && ` — ${call.management_company_name}`}
              </p>
            </div>
          </div>

          <div className="w-full xl:max-w-[48rem] xl:flex-none">
            <div className="flex flex-col gap-3 xl:items-end">
              <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                {surveyDone && (
                  <span className="badge-pill bg-emerald-100 text-emerald-800">Survey Done</span>
                )}
                {call.survey_email_sent && (
                  <span className="badge-pill bg-emerald-50 text-emerald-700">Review Sent</span>
                )}
                {call.termination_email_sent && (
                  <span className="badge-pill bg-rose-50 text-rose-700">Stop Sent</span>
                )}
              </div>

              <div className="flex flex-wrap gap-3 xl:justify-end">
                {surveyReady && (
                  <button
                    onClick={() => navigate(`/calls/${call.id}/survey`)}
                    className="button-primary min-w-[10rem]"
                  >
                    Fill Survey
                  </button>
                )}
                {surveyDone && (
                  <>
                    <button onClick={openPdfReport} className="button-accent min-w-[10rem]">
                      View Report
                    </button>
                    <button
                      onClick={() => navigate(`/calls/${call.id}/survey`)}
                      className="button-primary min-w-[10rem]"
                    >
                      Edit Survey
                    </button>
                    <button
                      onClick={openReviewEmailModal}
                      className={`${call.survey_email_sent
                        ? "button-ghost border-emerald-500 text-emerald-700"
                        : "button-primary"} min-w-[10rem]`}
                    >
                      {call.survey_email_sent ? "\u2713 Review Sent" : "Email Review"}
                    </button>
                    <button
                      onClick={openStopEmailModal}
                      className={`${call.termination_email_sent
                        ? "button-ghost border-rose-500 text-rose-700"
                        : "button-ghost"} min-w-[10rem]`}
                    >
                      {call.termination_email_sent ? "\u2713 Stop Sent" : "Stop Process"}
                    </button>
                  </>
                )}
              </div>

              <div className="flex flex-wrap gap-3 xl:justify-end">
                {!editing ? (
                  <button onClick={() => setEditing(true)} className="button-accent min-w-[10rem]">
                    Edit Call
                  </button>
                ) : (
                  <>
                    <button onClick={handleSave} disabled={saving} className="button-primary min-w-[10rem]">
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                    <button onClick={() => setEditing(false)} className="button-ghost min-w-[10rem]">
                      Cancel
                    </button>
                  </>
                )}
                <button onClick={handleReset} className="button-ghost min-w-[10rem]">
                  Reset
                </button>
                <button
                  onClick={async () => {
                    if (window.confirm("Archive this call?")) {
                      await callsApi.archive(call.id);
                      navigate("/calls");
                    }
                  }}
                  className="button-danger min-w-[10rem]"
                >
                  Archive
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Survey resolution info bar */}
      {surveyInfo && (
        <section className="surface-panel rise-in p-4 md:px-6">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="label-caps">Survey</span>
            {surveyInfo.survey_name ? (
              <span className="badge-pill bg-teal-100 text-teal-800">{surveyInfo.survey_name}</span>
            ) : (
              <span className="badge-pill bg-amber-100 text-amber-800">No survey configured for this HMC</span>
            )}
            {surveyInfo.management_company_name && (
              <span className="text-slate-500">via {surveyInfo.management_company_name} contract</span>
            )}
            {surveyDone && <span className="badge-pill bg-emerald-100 text-emerald-800">Response submitted</span>}
          </div>
        </section>
      )}

      {/* KPI cards */}
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="kpi-card rise-in px-6 py-5">
          <div className="label-caps">Status</div>
          {editing ? (
            <select
              value={editFields.status_id}
              onChange={(e) => setEditFields({ ...editFields, status_id: Number(e.target.value) })}
              className="select-shell mt-2"
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          ) : (
            <>
              <div className="mt-3 text-xl font-semibold tracking-tight">{call.status.name}</div>
              <div className="mt-3">
                <span className={`badge-pill ${statusTone(call.status.name)}`}>{call.call_type.name}</span>
              </div>
            </>
          )}
        </div>
        <div className="kpi-card rise-in delay-1 px-6 py-5">
          <div className="label-caps">Survey score</div>
          <div className="mt-3 text-xl font-semibold tracking-tight">
            {call.percent_achieved != null ? `${call.percent_achieved}%` : "Pending"}
          </div>
          {call.percent_achieved != null && (
            <div className="mt-4">
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${Math.min(call.percent_achieved, 100)}%` }} />
              </div>
            </div>
          )}
        </div>
        <div className="kpi-card rise-in delay-2 px-6 py-5">
          <div className="label-caps">Call cost</div>
          <div className="mt-3 text-xl font-semibold tracking-tight">${call.call_cost.toFixed(2)}</div>
          <div className="mt-3 text-sm text-slate-600">{call.is_payment_made ? "Payment marked" : "Awaiting payment"}</div>
        </div>
        <div className="kpi-card rise-in delay-3 px-6 py-5">
          <div className="label-caps">Scheduling</div>
          {editing ? (
            <div className="mt-2 space-y-2">
              <label className="text-xs text-slate-500">Scheduled</label>
              <input
                type="date"
                value={editFields.scheduled_call_date}
                onChange={(e) => setEditFields({ ...editFields, scheduled_call_date: e.target.value })}
                className="input-shell"
              />
              <label className="text-xs text-slate-500">Earliest</label>
              <input
                type="date"
                value={editFields.earliest_call_date}
                onChange={(e) => setEditFields({ ...editFields, earliest_call_date: e.target.value })}
                className="input-shell"
              />
            </div>
          ) : (
            <>
              <div className="mt-3 text-xl font-semibold tracking-tight">{call.scheduled_call_date || "Not set"}</div>
              <div className="mt-3 text-sm text-slate-600">Earliest: {call.earliest_call_date || "Not set"}</div>
            </>
          )}
        </div>
      </section>

      {/* Detail panels */}
      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr),minmax(0,0.9fr)]">
        <div className="surface-panel rise-in delay-1 p-6 md:p-7">
          <div className="eyebrow">Operational detail</div>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight">Call details</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="surface-muted p-4">
              <div className="label-caps">Order</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">
                {call.order_id ? `#${call.order_id}` : "Unlinked legacy call"}
              </div>
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Shop ID</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">{call.shop_id || "Not set"}</div>
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Service level</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">{call.service_level.name}</div>
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Contract year</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">{call.contract_year || "Not set"}</div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <div>
              <div className="label-caps">Scenario</div>
              {editing ? (
                <textarea
                  value={editFields.scenario}
                  onChange={(e) => setEditFields({ ...editFields, scenario: e.target.value })}
                  className="textarea-shell mt-2 min-h-[100px]"
                  rows={4}
                />
              ) : (
                <div className="surface-muted mt-2 p-4 text-sm leading-6 text-slate-600">
                  {call.scenario || "No scenario notes entered."}
                </div>
              )}
            </div>
            <div>
              <div className="label-caps">Notes</div>
              {editing ? (
                <textarea
                  value={editFields.notes}
                  onChange={(e) => setEditFields({ ...editFields, notes: e.target.value })}
                  className="textarea-shell mt-2 min-h-[100px]"
                  rows={4}
                />
              ) : (
                <div className="surface-muted mt-2 p-4 text-sm leading-6 text-slate-600">
                  {call.notes || "No call notes entered."}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="surface-panel rise-in delay-2 p-6 md:p-7">
            <div className="eyebrow">People and flags</div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight">Participants</h2>
            <div className="mt-6 space-y-4 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-4">
                <span>Sales staff</span>
                <span className="font-semibold text-slate-900">{call.sales_staff_name || "Unassigned"}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span>Shopper</span>
                {editing ? (
                  <select
                    value={editFields.shopper_id || ""}
                    onChange={(e) =>
                      setEditFields({ ...editFields, shopper_id: e.target.value ? Number(e.target.value) : null })
                    }
                    className="select-shell max-w-[14rem]"
                  >
                    <option value="">Unassigned</option>
                    {shopperList.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                ) : (
                  <span className={`font-semibold ${call.shopper_name ? "text-slate-900" : "text-amber-700"}`}>
                    {call.shopper_name || "Unassigned"}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {call.is_reshop_call && <span className="badge-pill bg-orange-100 text-orange-800">Reshop</span>}
                {call.is_overdue && <span className="badge-pill bg-rose-100 text-rose-800">Overdue</span>}
                {call.is_override && <span className="badge-pill bg-violet-100 text-violet-800">Override</span>}
                {call.is_survey_completed && <span className="badge-pill bg-emerald-100 text-emerald-800">Survey done</span>}
                {call.is_payment_made && <span className="badge-pill bg-teal-100 text-teal-800">Paid</span>}
              </div>
              {call.percent_achieved != null && (
                <div className="pt-3">
                  <div className="flex items-center justify-between gap-4">
                    <span>Score</span>
                    <span className={`badge-pill ${scoreTone(call.percent_achieved)}`}>{call.percent_achieved}%</span>
                  </div>
                  <div className="progress-track mt-3">
                    <div className="progress-fill" style={{ width: `${Math.min(call.percent_achieved, 100)}%` }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="surface-panel rise-in delay-3 p-6 md:p-7">
            <div className="eyebrow">Audio capture</div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight">Recording</h2>
            {playableAudioUrl ? (
              <div className="mt-5 surface-muted p-4">
                <audio controls src={playableAudioUrl} className="w-full" />
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span className="badge-pill bg-slate-100 text-slate-700">
                    {call.audio_source === "legacy" ? "Legacy archive" : "Uploaded file"}
                  </span>
                  {call.audio_source === "legacy" && call.legacy_wave_path && (
                    <span className="truncate">Path: {call.legacy_wave_path}</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-[1.2rem] border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                No recording is available yet. You can upload one now, or configure
                the backend `LEGACY_RECORDINGS_ROOT` to let migrated calls stream files
                from the legacy archive path.
              </div>
            )}
            <div className="mt-5 space-y-4">
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                className="input-shell"
              />
              <button onClick={handleUploadAudio} disabled={!audioFile} className="button-primary">
                {playableAudioUrl ? "Replace recording" : "Upload recording"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PDF Report Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/50" onClick={closePdfModal}>
          <div className="flex items-center justify-between px-6 py-3 bg-white border-b" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold">Survey Report — Call #{displayCallId}</h3>
            <div className="flex gap-2">
              {pdfUrl && (
                <>
                  <button
                    onClick={() => {
                      const a = document.createElement("a");
                      a.href = pdfUrl;
                      a.download = `SurveyReport_Call_${displayCallId}.pdf`;
                      a.click();
                    }}
                    className="button-ghost text-sm"
                  >
                    Download
                  </button>
                  <button
                    onClick={() => {
                      const w = window.open(pdfUrl);
                      if (w) { w.onload = () => w.print(); }
                    }}
                    className="button-ghost text-sm"
                  >
                    Print
                  </button>
                </>
              )}
              <button onClick={closePdfModal} className="button-ghost text-sm">Close</button>
            </div>
          </div>
          <div className="flex-1" onClick={(e) => e.stopPropagation()}>
            {pdfLoading ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-white text-lg">Generating report...</p>
              </div>
            ) : pdfUrl ? (
              <iframe src={pdfUrl} title="Survey Report" className="w-full h-full" />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-white">Unable to generate report.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit trail */}
      <section className="surface-panel rise-in delay-2 p-6 md:p-7">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="eyebrow">Audit trail</div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight">History</h2>
          </div>
          <span className="badge-pill bg-slate-900 text-white">{history.length} events</span>
        </div>

        {history.length === 0 ? (
          <div className="empty-state">
            <div>
              <div className="label-caps">No history yet</div>
              <p className="mt-2">Changes to this call will appear here as they happen.</p>
            </div>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-[1.2rem]">
            <table className="table-modern min-w-full">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Changed By</th>
                  <th>Field</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={entry.id}>
                    <td className="text-sm text-slate-600">{new Date(entry.created_at).toLocaleString()}</td>
                    <td>{entry.changed_by_name || "System"}</td>
                    <td className="font-semibold text-slate-900">{entry.field_changed}</td>
                    <td className="text-sm text-slate-600">{entry.old_value || "-"}</td>
                    <td className="text-sm text-slate-600">{entry.new_value || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Email Review Modal */}
      {showReviewEmail && call && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowReviewEmail(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 bg-slate-50 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Email Review Report</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Send survey review and recording to recipients
                  </p>
                </div>
                {emailScore != null && (
                  <span className={`badge-pill text-sm font-bold ${scoreTone(emailScore)}`}>
                    Score: {emailScore}%
                  </span>
                )}
              </div>
              {emailTemplateSource && (
                <p className="text-[10px] text-slate-400 mt-1">Template: {emailTemplateSource}</p>
              )}
            </div>
            <div className="p-5 space-y-4">
              {/* Recipient fields grid */}
              <div>
                <label className="label-caps text-[10px] block mb-2">Recipients</label>
                <div className="grid grid-cols-2 gap-2">
                  {emailRecipientFields.map((rf, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase w-20 shrink-0 text-right">{rf.label}</span>
                      <input
                        value={rf.email}
                        onChange={(e) => {
                          const updated = [...emailRecipientFields];
                          updated[i] = { ...updated[i], email: e.target.value };
                          setEmailRecipientFields(updated);
                        }}
                        className="input-shell text-sm flex-1"
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setEmailRecipientFields([...emailRecipientFields, { label: "CC", email: "" }])}
                  className="text-xs text-indigo-600 hover:underline mt-2"
                >
                  + Add recipient
                </button>
              </div>
              <div>
                <label className="label-caps text-[10px] block mb-1">Subject</label>
                <input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="input-shell w-full text-sm"
                />
              </div>
              <div>
                <label className="label-caps text-[10px] block mb-1">Body</label>
                <div
                  className="input-shell w-full text-sm min-h-[160px] p-3 prose prose-sm max-w-none overflow-y-auto"
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: emailBody }}
                  onBlur={(e) => setEmailBody(e.currentTarget.innerHTML)}
                />
              </div>
              {/* Links section */}
              <div className="flex gap-4 text-sm">
                <button
                  type="button"
                  onClick={() => {
                    openPdfReport();
                  }}
                  className="text-indigo-600 hover:underline font-medium"
                >
                  Shop Call Review (PDF)
                </button>
                {emailWaveUrl && (
                  <a
                    href={emailWaveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline font-medium"
                  >
                    Recorded Call
                  </a>
                )}
              </div>
            </div>
            <div className="p-5 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setShowReviewEmail(false)} className="button-ghost">Cancel</button>
              <button
                onClick={handleSendReviewEmail}
                disabled={emailSending || emailRecipientFields.length === 0}
                className="button-primary"
              >
                {emailSending ? "Sending..." : "Send Review Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stop Process Email Modal */}
      {showStopEmail && call && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowStopEmail(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 bg-rose-50 rounded-t-xl">
              <h3 className="text-lg font-semibold">Stop Process Notification</h3>
              <p className="text-xs text-slate-500 mt-0.5">Send termination / stop process email to sales staff</p>
              {emailTemplateSource && (
                <p className="text-[10px] text-slate-400 mt-1">Template: {emailTemplateSource}</p>
              )}
            </div>
            <div className="p-5 space-y-4">
              {/* Recipient fields grid */}
              <div>
                <label className="label-caps text-[10px] block mb-2">Recipients</label>
                <div className="grid grid-cols-2 gap-2">
                  {emailRecipientFields.map((rf, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase w-20 shrink-0 text-right">{rf.label}</span>
                      <input
                        value={rf.email}
                        onChange={(e) => {
                          const updated = [...emailRecipientFields];
                          updated[i] = { ...updated[i], email: e.target.value };
                          setEmailRecipientFields(updated);
                        }}
                        className="input-shell text-sm flex-1"
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setEmailRecipientFields([...emailRecipientFields, { label: "CC", email: "" }])}
                  className="text-xs text-indigo-600 hover:underline mt-2"
                >
                  + Add recipient
                </button>
              </div>
              <div>
                <label className="label-caps text-[10px] block mb-1">Subject</label>
                <input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="input-shell w-full text-sm"
                />
              </div>
              <div>
                <label className="label-caps text-[10px] block mb-1">Body</label>
                <div
                  className="input-shell w-full text-sm min-h-[160px] p-3 prose prose-sm max-w-none overflow-y-auto"
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: emailBody }}
                  onBlur={(e) => setEmailBody(e.currentTarget.innerHTML)}
                />
              </div>
            </div>
            <div className="p-5 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setShowStopEmail(false)} className="button-ghost">Cancel</button>
              <button
                onClick={handleSendStopEmail}
                disabled={emailSending || emailRecipientFields.length === 0}
                className="button-primary bg-rose-700 hover:bg-rose-800"
              >
                {emailSending ? "Sending..." : "Send Stop Process Email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
